import { Link } from '@design-system/Link';
import { Image } from '@design-system/Image';
import { Badge } from '@design-system/Badge';
import { cn } from '@/utils/styles';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Card, CardContent } from '@design-system/Card';
import { getFormulaImageUrl } from '@/utils/nutrition/getFormulaImageUrl';
import { formatSMPDate } from '@/components/screens/SMP/utils/smpHelpers';
import { SMP_PATH, SMP_DETAIL_PATH } from '@/constants/paths';
import { isEligibleForComponentization } from '@/utils/componentizationEligibility';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';
import type { Subscription } from '@viome/viome-base/src/usecase/Misc/MiscModel';

const SubscriptionStatus = {
  Active: 'Active',
  Cancelled: 'Cancelled',
  Paused: 'Paused',
} as const;

const getGradientClassByProduct = (imageUrl: string) => {
  const filename = imageUrl.split('/').pop() || '';

  switch (filename) {
    case 'gut_formula.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#EFE6B6_0%,#F0E9E3_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'toothpaste_and_gel.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#CCE3E8_0%,#EEE9E1_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'supplement.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#D5F6D9_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'oral_lozenges.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#DCF3FF_0%,#E0E2F0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    default:
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#ccc_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
  }
};

const getProductImageUrl = (imageUrl: string) =>
  imageUrl?.startsWith('http') ? imageUrl : getFormulaImageUrl(imageUrl);

interface Props {
  subscription: Subscription;
}

export const AccountComponentizedSubscriptionCard = ({ subscription }: Props) => {
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();
  const targetPath =
    isComponentizationEnabled &&
    isEligibleForComponentization(subscription, eligibleSkus)
      ? SMP_DETAIL_PATH
      : SMP_PATH;
  const isKit = (subscription.category ?? '').toUpperCase() === 'KIT';
  const products =
    subscription.products?.filter((product) => product.is_last_subscribed) || [];
  const product = products[0];
  const status = subscription.subscription_status;

  const title =
    subscription.sku_name ||
    (products.length === 1
      ? product.name || subscription.sku
      : subscription.sku || 'Subscription');

  const supplyText = isKit ? 'Annual supply' : '30-day supply';
  const tileClass = isKit ? 'size-16' : 'size-16';
  const imageClass = isKit ? 'size-20' : 'size-10';

  return (
    <Link
      to={`${targetPath}?subscriptionId=${subscription.subscription_id}`}
      variant="flat"
      className="block"
      data-testid="accountComponentized-subscriptionCard"
    >
      <Card className="border border-stroke-static">
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="typography-h2 text-primary font-sans font-medium">
              {title}
            </span>
            {status && (
              <Badge
                size="sm"
                className={cn(
                  'capitalize shrink-0',
                  status === SubscriptionStatus.Active &&
                    'bg-[#009E82] text-white',
                  status === SubscriptionStatus.Cancelled &&
                    'bg-tertiary text-white',
                  status === SubscriptionStatus.Paused && 'bg-black text-white',
                )}
              >
                {status}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-4">
            {product && (
              <div
                className={cn(
                  'relative rounded-md overflow-hidden flex items-center justify-center shrink-0',
                  tileClass,
                  getGradientClassByProduct(product.image_url),
                )}
              >
                <Image
                  src={getProductImageUrl(product.image_url)}
                  alt={product.name}
                  className={cn('object-contain', imageClass)}
                />
              </div>
            )}

            <div className="flex flex-col gap-1 min-w-0 flex-1">
              {subscription.next_shipment_date && (
                <span className="typography-body text-secondary">
                  Next order: {formatSMPDate(subscription.next_shipment_date)}
                </span>
              )}

              {subscription.renewal_interval ? (
                <span className="typography-body text-secondary">
                  {subscription.renewal_interval}
                </span>
              ) : (
                <span className="typography-body text-secondary">{supplyText}</span>
              )}
            </div>
          </div>

          <div
            className="flex items-center justify-between"
            data-testid="accountComponentized-manageSubscription"
          >
            <span className="typography-body text-primary">
              Manage subscription
            </span>
            <ChevronRightIcon className="size-6 text-tertiary" aria-hidden />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};